import { createContext, useCallback, useContext, useMemo } from 'react';
import type { CrossPartnerUserSettings } from './hooks/use-cross-partner-user-settings';
import { SidePanelPersonalizationViewModel } from './hooks/use-side-panel-personalization';
import { SidePanelSurfaceConfig } from './model/descriptors';
import { SidePanelPropertyKey, SidePanelSectionKey } from './model/layout.types';

export const CrossPartnerUserSettingsContext = createContext<CrossPartnerUserSettings | null>(
  null,
);

export function useCrossPartnerUserSettingsContext(): CrossPartnerUserSettings {
  const value = useContext(CrossPartnerUserSettingsContext);

  if (!value) {
    throw new Error(
      'useCrossPartnerUserSettingsContext must be used within CrossPartnerUserSettingsProvider',
    );
  }

  return value;
}

export interface SidePanelPersonalizationContextValue {
  surface: SidePanelSurfaceConfig;
  viewModel: SidePanelPersonalizationViewModel;
}

export const SidePanelPersonalizationContext =
  createContext<SidePanelPersonalizationContextValue | null>(null);

export function usePersonalizedSidePanel(): SidePanelPersonalizationContextValue {
  const value = useContext(SidePanelPersonalizationContext);

  if (!value) {
    throw new Error(
      'usePersonalizedSidePanel must be used within SidePanelPersonalizationProvider',
    );
  }

  return value;
}

/**
 * The set of visible field keys for a card-rendered section. `null` means no
 * personalization applies — the surface has no provider (Sites/Clients pages)
 * or the view model is still resolving — and the card must render everything.
 */
export function useSidePanelSectionLayout(
  sectionKey: SidePanelSectionKey,
): Set<SidePanelPropertyKey> | null {
  const context = useContext(SidePanelPersonalizationContext);
  const section = context?.viewModel.effectiveLayout.sections[sectionKey];
  const loading = context?.viewModel.loading;

  return useMemo(() => {
    if (!context || loading) {
      return null;
    }

    return new Set(
      (section?.properties ?? []).filter((entry) => entry.visible).map((entry) => entry.key),
    );
  }, [context, loading, section]);
}

/**
 * Ordered property keys (visible or not) for a card-rendered section that
 * applies the layout order itself — pair with the section's `reorderable`
 * opt-in. `null` means no personalization applies (no provider / still
 * loading) and the card keeps its built-in order.
 */
export function useSidePanelSectionOrder(
  sectionKey: SidePanelSectionKey,
): SidePanelPropertyKey[] | null {
  const context = useContext(SidePanelPersonalizationContext);
  const section = context?.viewModel.effectiveLayout.sections[sectionKey];
  const loading = context?.viewModel.loading;

  return useMemo(() => {
    if (!context || loading) {
      return null;
    }

    return (section?.properties ?? []).map((entry) => entry.key);
  }, [context, loading, section]);
}

/**
 * Predicate form of {@link useSidePanelSectionLayout} for card-rendered
 * sections. `isVisible(key, isEmpty?)` is true when the field should render:
 * the key is in the visible set AND it isn't dropped by the "hide empty fields"
 * toggle. A `null` layout (no provider / still loading) renders everything.
 *
 * Card-rendered fields can't go through the panel's `hideEmpty`/`isEmpty`
 * pipeline (that path requires a `Row`), so the card passes the emptiness it
 * alone knows (blank value, unassigned owner…) and the engine applies the
 * toggle centrally. Any card gates a field with `!isVisible(key, isEmpty)`.
 */
export function useSectionVisibility(
  sectionKey: SidePanelSectionKey,
): (key: SidePanelPropertyKey, isEmpty?: boolean) => boolean {
  const visibleKeys = useSidePanelSectionLayout(sectionKey);
  const hideEmpty =
    useContext(SidePanelPersonalizationContext)?.viewModel.effectiveLayout.hideEmpty ?? false;

  return useCallback(
    (key: SidePanelPropertyKey, isEmpty = false) => {
      if (visibleKeys === null) {
        return true;
      }

      return visibleKeys.has(key) && !(hideEmpty && isEmpty);
    },
    [visibleKeys, hideEmpty],
  );
}

/** Visible card-rendered (no `Row`) `ca:` keys in layout order; none without a provider — attributes are opt-in. */
export function useCardCustomAttributeKeys(
  sectionKey: SidePanelSectionKey,
): SidePanelPropertyKey[] {
  const context = useContext(SidePanelPersonalizationContext);
  const section = context?.viewModel.loading
    ? undefined
    : context?.viewModel.sections.find((candidate) => candidate.key === sectionKey);

  return useMemo(
    () =>
      (section?.properties ?? [])
        .filter(
          ({ descriptor, visible }) =>
            visible && !descriptor.Row && descriptor.key.startsWith('ca:'),
        )
        .map(({ descriptor }) => descriptor.key),
    [section],
  );
}
